import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useDropzone } from 'react-dropzone';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle, ArrowRight, Building, UserPlus, Link, Users } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { ColumnMapper } from '@/components/ui/column-mapper';
import { supabase } from '@/integrations/supabase/client';
import {
  CONTACT_DB_FIELDS,
  generateSmartMappings,
  transformDataWithMapping,
  validateMappings,
  fetchCustomContactFields,
  mergeContactFieldLists,
  type ColumnMapping,
  type ContactDatabaseField,
} from '@/lib/contact-mapping-utils';

interface ContactUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: number | string;
  clientName?: string;
  onUploadComplete: () => void;
}

export function ContactUploadDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  onUploadComplete
}: ContactUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<any[][]>([]);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [currentStep, setCurrentStep] = useState<'upload' | 'mapping' | 'preview'>('upload');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [preview, setPreview] = useState<any[]>([]);
  const [uploadResults, setUploadResults] = useState<any>(null);
  const [uploadStats, setUploadStats] = useState({ created: 0, matched: 0, errors: 0, processed: 0, total: 0 });

  // Custom fields from DB
  const [customFields, setCustomFields] = useState<ContactDatabaseField[]>([]);
  const allDbFields = useMemo(() => mergeContactFieldLists(customFields), [customFields]);

  // Load custom fields when dialog opens
  useEffect(() => {
    if (open) {
      fetchCustomContactFields().then(setCustomFields).catch(console.error);
    }
  }, [open]);

  const downloadTemplate = () => {
    const headers = allDbFields.map(field => field.name);
    const example = [
      'John',
      'Smith',
      'john.smith@acme.com',
      'VP of Marketing',
      'Marketing',
      'Senior',
      '+1-555-0123',
      'https://linkedin.com/in/johnsmith',
      'John Smith',
      'csv_import',
      'Acme Corp',
      'acme.com',
      'https://linkedin.com/company/acme',
    ];

    const csvContent = headers.join(',') + '\n' + example.join(',');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    setFile(file);
    parseFile(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    multiple: false
  });

  const parseFile = async (file: File) => {
    try {
      setErrors([]);
      const text = await file.text();
      // RFC-4180 compliant CSV parser (handles multi-line quoted fields, commas in quotes, escaped quotes)
      const parseCSV = (csvText: string): string[][] => {
        const rows: string[][] = [];
        let currentRow: string[] = [];
        let currentField = '';
        let inQuotes = false;

        for (let i = 0; i < csvText.length; i++) {
          const char = csvText[i];
          const nextChar = i + 1 < csvText.length ? csvText[i + 1] : '';

          if (inQuotes) {
            if (char === '"' && nextChar === '"') {
              currentField += '"'; // escaped quote
              i++;
            } else if (char === '"') {
              inQuotes = false; // end of quoted field
            } else {
              currentField += char; // content inside quotes (including newlines)
            }
          } else {
            if (char === '"') {
              inQuotes = true; // start of quoted field
            } else if (char === ',') {
              currentRow.push(currentField.trim());
              currentField = '';
            } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
              if (char === '\r') i++; // skip \r in \r\n
              currentRow.push(currentField.trim());
              // Only add non-empty rows
              if (currentRow.some(cell => cell !== '')) {
                rows.push(currentRow);
              }
              currentRow = [];
              currentField = '';
            } else {
              currentField += char;
            }
          }
        }
        // Handle last row (no trailing newline)
        if (currentField || currentRow.length > 0) {
          currentRow.push(currentField.trim());
          if (currentRow.some(cell => cell !== '')) {
            rows.push(currentRow);
          }
        }
        return rows;
      };

      const allRows = parseCSV(text);
      if (allRows.length < 2) {
        setErrors(['File must contain at least a header row and one data row']);
        return;
      }

      const headers = allRows[0].map(h => h.trim());
      const data = allRows.slice(1);

      if (data.length === 0) {
        setErrors(['No data rows found in file']);
        return;
      }

      // Enforce max upload limit
      if (data.length > 50000) {
        setErrors([`File contains ${data.length} rows. Maximum 50,000 contacts per upload.`]);
        return;
      }

      setCsvHeaders(headers);
      setCsvData(data);

      // Generate smart mappings
      const smartMappings = generateSmartMappings(headers, allDbFields);

      // Add sample data to mappings
      const mappingsWithSamples = smartMappings.map((mapping, index) => ({
        ...mapping,
        sample: data.slice(0, 2).map(row => row[index]).filter(val => val && val.trim()).join(', ') || 'No data'
      }));

      setMappings(mappingsWithSamples);
      setCurrentStep('mapping');
    } catch (error) {
      console.error('Error parsing file:', error);
      setErrors(['Failed to parse file. Please check the format.']);
    }
  };

  const handleMappingChange = (csvColumn: string, dbField: string | null) => {
    setMappings(prev => prev.map(mapping =>
      mapping.csvColumn === csvColumn
        ? { ...mapping, dbField, isRequired: allDbFields.find(f => f.name === dbField)?.required || false }
        : mapping
    ));
  };

  const generatePreview = async () => {
    const validation = validateMappings(mappings, allDbFields);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    if (validation.warnings.length > 0) {
      toast({
        title: "Mapping Warnings",
        description: validation.warnings.join('. '),
        variant: "default",
      });
    }

    try {
      setUploading(true);
      setUploadProgress(50);

      // Transform data using column mappings
      // Company matching + dedup are handled server-side by the ingest-contacts Edge Function
      const transformedData = transformDataWithMapping(csvData, mappings, allDbFields);

      // Filter out rows with no identifiable data
      const validContacts = transformedData.filter(contact =>
        contact.first_name || contact.last_name || contact.primary_email || contact.full_name
      );

      setPreview(validContacts);
      setErrors([]);
      setCurrentStep('preview');
      setUploadProgress(100);

      const skipped = transformedData.length - validContacts.length;
      toast({
        title: "Preview Generated",
        description: `${validContacts.length} contacts ready to upload${skipped > 0 ? `. ${skipped} rows skipped (missing name/email).` : '.'}`,
      });

    } catch (error) {
      console.error('Error generating preview:', error);
      setErrors(['Error processing data. Please check your mappings.']);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const BATCH_SIZE = 1000;

  const handleUpload = async () => {
    if (preview.length === 0) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      // Clean empty values from each contact before sending
      const contacts = preview.map(contact => {
        const cleaned: Record<string, any> = {};
        for (const [key, value] of Object.entries(contact)) {
          if (value !== null && value !== undefined && value !== '') {
            cleaned[key] = value;
          }
        }
        return cleaned;
      });

      // Split into batches for reliable processing
      const totalBatches = Math.ceil(contacts.length / BATCH_SIZE);
      let totalCreated = 0, totalMatched = 0, totalErrors = 0;
      let companiesCreated = 0, companiesMatched = 0;

      setUploadStats({ created: 0, matched: 0, errors: 0, processed: 0, total: contacts.length });

      for (let i = 0; i < contacts.length; i += BATCH_SIZE) {
        const batch = contacts.slice(i, i + BATCH_SIZE);
        const batchNum = Math.floor(i / BATCH_SIZE) + 1;

        try {
          const { data, error } = await supabase.functions.invoke('ingest-contacts-bulk', {
            body: {
              client_id: String(clientId),
              contacts: batch,
            },
          });

          if (error) {
            console.error(`Batch ${batchNum}/${totalBatches} failed:`, error);
            totalErrors += batch.length;
          } else if (data) {
            totalCreated += data.created || 0;
            totalMatched += data.matched || 0;
            totalErrors += data.errors || 0;
            companiesCreated += data.companies_created || 0;
            companiesMatched += data.companies_matched || 0;
          }
        } catch (batchErr) {
          console.error(`Batch ${batchNum}/${totalBatches} exception:`, batchErr);
          totalErrors += batch.length;
        }

        const processed = Math.min(i + BATCH_SIZE, contacts.length);
        const progress = Math.round((processed / contacts.length) * 100);
        setUploadProgress(progress);
        setUploadStats({
          created: totalCreated,
          matched: totalMatched,
          errors: totalErrors,
          processed,
          total: contacts.length,
        });
      }

      setUploadProgress(100);
      setUploadResults({
        total: contacts.length,
        created: totalCreated,
        matched: totalMatched,
        errors: totalErrors,
        companies_created: companiesCreated,
        companies_matched: companiesMatched,
      });

      const parts = [
        `${totalCreated} created`,
        `${totalMatched} matched`,
      ];
      if (companiesCreated > 0) parts.push(`${companiesCreated} new accounts`);
      if (companiesMatched > 0) parts.push(`${companiesMatched} accounts matched`);
      if (totalErrors > 0) parts.push(`${totalErrors} errors`);

      toast({
        title: "Upload Complete",
        description: parts.join(', ') + '.',
      });

      onUploadComplete();
    } catch (error) {
      console.error('Upload failed:', error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Please try again or check your file format.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const resetDialog = () => {
    setFile(null);
    setCsvHeaders([]);
    setCsvData([]);
    setMappings([]);
    setPreview([]);
    setUploadResults(null);
    setErrors([]);
    setUploadProgress(0);
    setUploading(false);
    setCurrentStep('upload');
    onOpenChange(false);
  };

  const validation = mappings.length > 0 ? validateMappings(mappings, allDbFields) : { isValid: false, errors: [], warnings: [] };

  // Count contacts that have company info for preview summary
  const contactsWithCompanyInfo = preview.filter(c => c.company_name || c.company_domain || c.company_linkedin).length;
  const contactsWithoutCompanyInfo = preview.length - contactsWithCompanyInfo;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Contacts CSV</DialogTitle>
        </DialogHeader>

        <Tabs value={currentStep} className="flex-1 flex flex-col">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="upload" disabled={currentStep !== 'upload'}>
              1. Upload File
            </TabsTrigger>
            <TabsTrigger value="mapping" disabled={currentStep === 'upload'}>
              2. Map Columns
            </TabsTrigger>
            <TabsTrigger value="preview" disabled={currentStep !== 'preview'}>
              3. Preview & Upload
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            <TabsContent value="upload" className="space-y-6 mt-6">
              {/* Template Download */}
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Upload a CSV file with contact data. Contacts will be automatically matched to existing accounts or new accounts will be created.
                </p>
                <Button variant="outline" onClick={downloadTemplate}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>

              {/* Account Matching Info */}
              <Alert>
                <Link className="h-4 w-4" />
                <AlertDescription>
                  <strong>Account Matching:</strong> Contacts will be automatically linked to accounts using:
                  <br />1. LinkedIn company URL (highest priority)
                  <br />2. Company domain matching
                  <br />3. Create new account if no match found
                </AlertDescription>
              </Alert>

              {/* File Drop Zone */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-4" />
                {isDragActive ? (
                  <p>Drop the file here...</p>
                ) : (
                  <div>
                    <p className="mb-2">Drag and drop a CSV file here, or click to select</p>
                    <p className="text-sm text-muted-foreground">Supports CSV, XLS, and XLSX files</p>
                  </div>
                )}
                {file && (
                  <p className="mt-2 text-sm font-medium text-primary">
                    Selected: {file.name}
                  </p>
                )}
              </div>

              {/* Errors */}
              {errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-disc ml-4">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            <TabsContent value="mapping" className="mt-6">
              {csvHeaders.length > 0 && (
                <ColumnMapper
                  csvHeaders={csvHeaders}
                  csvData={csvData}
                  dbFields={allDbFields}
                  targetTable="contacts"
                onFieldCreated={(field, forCsvColumn) => {
                  setCustomFields(prev => [...prev, field as ContactDatabaseField]);
                  // Auto-map the CSV column to the new field
                  setMappings(prev => prev.map(m =>
                    m.csvColumn === forCsvColumn
                      ? { ...m, dbField: field.name, isRequired: false, dataType: field.type, confidence: 'none' as const }
                      : m
                  ));
                }}
                  mappings={mappings}
                  onMappingChange={handleMappingChange}
                />
              )}
            </TabsContent>

            <TabsContent value="preview" className="space-y-6 mt-6">
              {/* Pre-upload Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-600">Total Contacts</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">{preview.length}</p>
                </div>
                <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Building className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-600">With Company Info</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">{contactsWithCompanyInfo}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-950/30 p-4 rounded-lg text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <UserPlus className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-600">Without Company</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-600">{contactsWithoutCompanyInfo}</p>
                </div>
              </div>

              {preview.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <h3 className="font-medium">Preview: {preview.length} contacts ready to upload</h3>
                  </div>

                  <div className="max-h-60 overflow-y-auto border rounded">
                    <table className="min-w-full divide-y divide-border">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                            Name
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                            Email
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                            Job Title
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                            Company
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {preview.slice(0, 10).map((contact, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm font-medium">
                              {contact.first_name} {contact.last_name}
                            </td>
                            <td className="px-4 py-2 text-sm">{contact.primary_email || '-'}</td>
                            <td className="px-4 py-2 text-sm">{contact.title || '-'}</td>
                            <td className="px-4 py-2 text-sm">
                              {contact.company_name || contact.company_domain || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {preview.length > 10 && (
                      <p className="text-center text-sm text-muted-foreground p-2">
                        ... and {preview.length - 10} more contacts
                      </p>
                    )}
                  </div>

                  <Alert>
                    <Building className="h-4 w-4" />
                    <AlertDescription>
                      Company matching and contact deduplication will be handled automatically during upload.
                      Contacts with company info will be matched to existing accounts or new accounts will be created.
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {/* Upload Progress */}
              {uploading && currentStep === 'preview' && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Uploading contacts... {uploadStats.processed > 0 ? `${uploadStats.processed.toLocaleString()} / ${uploadStats.total.toLocaleString()}` : ''}</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} />
                  {uploadStats.processed > 0 && (
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      <span className="text-green-600">✓ {uploadStats.created.toLocaleString()} created</span>
                      <span className="text-blue-600">↻ {uploadStats.matched.toLocaleString()} matched</span>
                      {uploadStats.errors > 0 && <span className="text-red-600">✗ {uploadStats.errors.toLocaleString()} errors</span>}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={resetDialog} disabled={uploading}>
              Cancel
            </Button>
            <div className="flex gap-2">
              {currentStep === 'mapping' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep('upload')}
                    disabled={uploading}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={generatePreview}
                    disabled={!validation.isValid || uploading}
                  >
                    {uploading ? 'Processing...' : 'Preview'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
              {currentStep === 'preview' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep('mapping')}
                    disabled={uploading}
                  >
                    Back to Mapping
                  </Button>
                  <Button
                    onClick={handleUpload}
                    disabled={preview.length === 0 || uploading}
                  >
                    {uploading ? 'Uploading...' : `Upload ${preview.length} Contacts`}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
